/**
 * FeatureToggleContext
 *
 * Loads platform_feature_toggles once at app startup and caches them.
 * Components use useFeatureToggle('feature_key') to check toggle state.
 *
 * Pattern: single fetch at mount, no realtime subscription (toggles change rarely).
 * A page refresh propagates any admin change.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { fetchFeatureToggles, isFeatureActive, FeatureToggle } from '../services/featureToggleService';
import { logger } from '@deskbay/shared';

interface FeatureToggleContextType {
    /** All loaded toggles */
    toggles: FeatureToggle[];
    /** Check if a feature is active by key. Defaults to true if not found. */
    isActive: (featureKey: string) => boolean;
    /** Whether toggles have been loaded */
    isLoaded: boolean;
    /** Force re-fetch toggles (e.g. after admin change) */
    refresh: () => Promise<void>;
}

const FeatureToggleContext = createContext<FeatureToggleContextType | undefined>(undefined);

export function FeatureToggleProvider({ children }: { children: React.ReactNode }) {
    const [toggles, setToggles] = useState<FeatureToggle[]>([]);
    const [isLoaded, setIsLoaded] = useState(false);

    const loadToggles = useCallback(async () => {
        const data = await fetchFeatureToggles();
        setToggles(data);
        setIsLoaded(true);
        if (data.length > 0) {
            logger.api.debug(`Feature toggles loaded: ${data.length}`, 'FeatureToggleContext');
        }
    }, []);

    useEffect(() => {
        loadToggles();
    }, [loadToggles]);

    const isActive = useCallback(
        (featureKey: string): boolean => isFeatureActive(toggles, featureKey),
        [toggles]
    );

    const refresh = useCallback(async () => {
        await loadToggles();
    }, [loadToggles]);

    return (
        <FeatureToggleContext.Provider value={{ toggles, isActive, isLoaded, refresh }}>
            {children}
        </FeatureToggleContext.Provider>
    );
}

/**
 * Hook to access all feature toggles.
 */
export function useFeatureToggles(): FeatureToggleContextType {
    const context = useContext(FeatureToggleContext);
    if (context === undefined) {
        throw new Error('useFeatureToggles must be used within a FeatureToggleProvider');
    }
    return context;
}

/**
 * Convenience hook: check a single feature toggle by key.
 * Returns true if toggle is active or not found (safe default).
 */
export function useFeatureToggle(featureKey: string): boolean {
    const { isActive } = useFeatureToggles();
    return isActive(featureKey);
}
